/**
 * V71: 공통 페이지네이션 컴포넌트
 *
 * FE 슬라이싱 기반 페이지네이션 UI.
 * data.slice(start, end) 방식으로 사용.
 */

import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

export interface PaginationProps {
  currentPage: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

export function Pagination({ currentPage, totalItems, pageSize, onPageChange }: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  if (totalPages <= 1) return null;

  // 표시할 페이지 번호 범위 (최대 5개)
  const maxVisible = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let endPage = startPage + maxVisible - 1;
  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = Math.max(1, endPage - maxVisible + 1);
  }

  const pages: number[] = [];
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <div style={styles.container}>
      <button
        onClick={() => onPageChange(1)}
        disabled={currentPage === 1}
        style={{
          ...styles.button,
          ...(currentPage === 1 ? styles.buttonDisabled : {}),
        }}
        title="첫 페이지"
      >
        <ChevronsLeft size={16} />
      </button>
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        style={{
          ...styles.button,
          ...(currentPage === 1 ? styles.buttonDisabled : {}),
        }}
        title="이전 페이지"
      >
        <ChevronLeft size={16} />
      </button>

      {pages.map((page) => (
        <button
          key={page}
          onClick={() => onPageChange(page)}
          style={{
            ...styles.pageButton,
            ...(page === currentPage ? styles.pageButtonActive : {}),
          }}
        >
          {page}
        </button>
      ))}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        style={{
          ...styles.button,
          ...(currentPage === totalPages ? styles.buttonDisabled : {}),
        }}
        title="다음 페이지"
      >
        <ChevronRight size={16} />
      </button>
      <button
        onClick={() => onPageChange(totalPages)}
        disabled={currentPage === totalPages}
        style={{
          ...styles.button,
          ...(currentPage === totalPages ? styles.buttonDisabled : {}),
        }}
        title="마지막 페이지"
      >
        <ChevronsRight size={16} />
      </button>
    </div>
  );
}

/** 현재 페이지에 해당하는 데이터 슬라이스를 반환 */
export function paginateData<T>(data: T[], currentPage: number, pageSize: number): T[] {
  const start = (currentPage - 1) * pageSize;
  return data.slice(start, start + pageSize);
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '4px',
    padding: '16px 0',
  },
  button: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    border: '1px solid var(--color-gray-200)',
    borderRadius: '6px',
    background: 'white',
    color: 'var(--color-gray-700)',
    cursor: 'pointer',
    fontSize: '14px',
  },
  buttonDisabled: {
    color: 'var(--color-gray-300)',
    cursor: 'not-allowed',
    background: 'var(--color-gray-50)',
  },
  pageButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '32px',
    height: '32px',
    padding: '0 6px',
    border: '1px solid var(--color-gray-200)',
    borderRadius: '6px',
    background: 'white',
    color: 'var(--color-gray-700)',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 400,
  },
  pageButtonActive: {
    background: 'var(--color-blue-600)',
    color: 'white',
    borderColor: 'var(--color-blue-600)',
    fontWeight: 600,
  },
};
